import { computed, PropType } from 'vue';

export type Size = 'default' | 'large' | 'small';
export type Theme =
  | 'blue'
  | 'dark-yellow'
  | 'gray'
  | 'green'
  | 'red'
  | 'transparent'
  | 'white'
  | 'yellow';

export const buttonStyleProps = {
  disabled: { required: false, type: Boolean, default: false },
  fullWidth: { required: false, type: Boolean, default: false },
  isLoading: { required: false, type: Boolean, default: false },
  size: { required: false, type: String as PropType<Size>, default: 'default' },
  theme: { required: false, type: String as PropType<Theme>, default: 'green' },
};

interface ButtonStyleProps {
  fullWidth?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  size?: Size;
  theme?: Theme;
}

export default function useButtonStyle(props: ButtonStyleProps) {
  const borderUtilities = computed(() => {
    const utilities: string[] = ['border'];
    if (props.theme === 'white') {
      utilities.push('border-solid', 'hover:border-transparent', 'border-neutral-300');
    } else if (props.theme === 'transparent') {
      utilities.push('border-solid', 'border-black');
    } else {
      utilities.push('border-none');
    }
    utilities.push('rounded-full');
    return utilities.join(' ');
  });

  const colorUtilities = computed(() => {
    const utilities: string[] = [];
    if (props.theme === 'green') {
      utilities.push('bg-nuts-lime-800', 'text-white');
      if (!props.disabled) utilities.push('hover:bg-nuts-lime-900');
    }
    if (props.theme === 'gray') {
      utilities.push('bg-black', 'text-white');
      if (!props.disabled) utilities.push('hover:bg-nuts-neutral-700');
    }
    if (props.theme === 'red') {
      utilities.push('bg-nuts-red-800', 'text-white');
      if (!props.disabled) utilities.push('hover:bg-nuts-red-900');
    }
    if (props.theme === 'transparent') {
      utilities.push('bg-transparent', 'text-black');
    }
    if (props.theme === 'white') {
      utilities.push('bg-white', 'text-black');
      if (!props.disabled) utilities.push('hover:bg-neutral-100');
    }
    if (props.theme === 'yellow') {
      utilities.push('bg-yellow-200', 'text-black');
    }
    if (props.theme === 'dark-yellow') {
      utilities.push('bg-nuts-amber-400', 'text-black');
      if (!props.disabled) utilities.push('hover:bg-nuts-amber-500');
    }
    if (props.theme === 'blue') {
      utilities.push('bg-cyan-700', 'text-white');
      if (!props.disabled) utilities.push('hover:bg-cyan-800');
    }

    if (props.disabled || props.isLoading) utilities.push('opacity-50');

    utilities.push('duration-400', 'hover:shadow-sharp', 'transition-colors');

    return utilities.join(' ');
  });

  const fontColorUtilities = computed(() => {
    const utilities: string[] = [];
    if (props.theme === 'green') {
      utilities.push('text-white');
    }
    if (props.theme === 'gray') {
      utilities.push('text-white');
    }
    if (props.theme === 'red') {
      utilities.push('text-white');
    }
    if (props.theme === 'white') {
      utilities.push('text-black');
    }
    if (props.theme === 'yellow') {
      utilities.push('text-black');
    }
    if (props.theme === 'blue') {
      utilities.push('text-white');
    }
    return utilities.join(' ');
  });

  const widthUtilities = computed(() => {
    if (props.fullWidth) return 'w-full';
    if (!props.size) return '';

    const buttonVariants: Record<Size, string> = {
      default: 'px-7 md:px-8',
      large: 'px-8 md:px-9',
      small: 'px-5 md:px-6',
    };
    const buttonLoadingVariants: Record<Size, string> = {
      default: 'px-5',
      large: 'px-6',
      small: 'px-3',
    };

    if (props.isLoading) return buttonLoadingVariants[props.size];
    return buttonVariants[props.size];
  });

  const heightUtilities = computed(() => {
    if (!props.size) return '';

    const buttonHeightVariants: { readonly [key: string]: string } = {
      default: 'py-3',
      large: 'py-3',
      small: 'py-2 md:py-3',
    };

    return buttonHeightVariants[props.size];
  });

  const fontSizeUtilities = computed(() => {
    const defaults = 'md:leading-none leading-none font-semibold';
    if (!props.size) return defaults;

    const buttonFontSizeVariants: Record<Size, string> = {
      default: 'text-sm md:text-base',
      large: 'text-base md:text-lg',
      small: 'text-xs',
    };

    return `${buttonFontSizeVariants[props.size]} ${defaults}`;
  });

  return {
    borderUtilities,
    colorUtilities,
    cursorUtilities: ['md:cursor-pointer', 'disabled:cursor-not-allowed'],
    fontColorUtilities,
    fontSizeUtilities,
    heightUtilities,
    widthUtilities,
  };
}
